/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual } from 'react-redux';
import { Box, Typography, Button } from '@mui/material';
import { useSelector, dispatch as reduxDispatch } from 'src/redux/store';
import { Iconify } from 'src/components';
import {
    fetchClinicianConversionData,
    resetAllPracticesClinicianSlot,
} from 'src/redux/slices/analyticsReports';
import { reportDayStart, reportDayEnd } from '../reportMetricSpecs';
import ClinicianConversionTracker from './ClinicianConversionTracker';

/**
 * Clinician Conversion Tracker for the Analytics Reports "All Practices" page —
 * sits directly after Treatment Status Matrix, Practices view only.
 *
 * Scoped to the page's FULL `selectedPracticeIds` selection — no secondary
 * single-practice dropdown. When 2+ practices are selected, the underlying
 * fetchClinicianConversionData thunk (slot: 'allPractices') fans the fetch out
 * per selected practice and unions the results: every practitioner row is
 * tagged with its own `practiceId`/`practiceName` (row identity = practice +
 * practitioner — the same clinician at two practices, or two different
 * clinicians who share a name across practices, never collapse into one row),
 * and totals/the per-practitioner popup/CSV export all follow that union.
 * `ClinicianConversionTracker` renders the "Practice" column and an optional
 * toolbar Practice filter (defaulting to "All selected practices") whenever
 * the data spans more than one practice — see that component for the filter.
 *
 * A single selected practice behaves exactly as before (the thunk's
 * single-practice code path is untouched byte-for-byte).
 *
 * Writes to a SEPARATE Redux slot (`allPracticesClinicianConversion` /
 * `_allPracticesClinicianFetchKey`) from the By Practitioner page's
 * `clinicianConversion` / `_clinicianFetchKey`, so this page's fetches can
 * never disturb that other page's cached data — see fetchClinicianConversionData's
 * `slot` option and its per-slot stale-response token guard.
 */
export default function AllPracticesClinicianSection({
    practices,
    selectedPracticeIds,
    dateRange,
    maskName,
}) {
    const {
        slotState,
        fetchKey,
    } = useSelector(
        (state) => ({
            slotState: state.analyticsReports?.allPracticesClinicianConversion || {},
            fetchKey: state.analyticsReports?._allPracticesClinicianFetchKey,
        }),
        shallowEqual
    );

    // Normalize the page's {startDate,endDate} into {start,end} ONCE — used for
    // BOTH the desired fetch key and the dispatch (mirrors
    // AnalyticsReportsByPractitioner's date-normalize pattern).
    const normalizedDateRange = useMemo(() => {
        if (!dateRange?.startDate || !dateRange?.endDate) return null;
        return {
            start: reportDayStart(dateRange.startDate),
            end: reportDayEnd(dateRange.endDate),
        };
    }, [dateRange?.startDate, dateRange?.endDate]);

    // The page-selected practices are the SOLE scope — the whole array (not
    // just the first id) is folded into the fetch key so switching the
    // selected set, or the date range, always produces a distinct key and
    // therefore a genuine refetch. No stale rows/totals can survive a
    // deselect: the thunk's fan-out only ever queries `selectedPracticeIds`.
    const practiceIdsKey = selectedPracticeIds && selectedPracticeIds.length > 0
        ? selectedPracticeIds.join(',')
        : null;
    const desiredKey = (practiceIdsKey && normalizedDateRange)
        ? `${practiceIdsKey}|${normalizedDateRange.start}|${normalizedDateRange.end}`
        : null;

    // Display-name map for every selected practice, built once per selection
    // change — passed to the thunk so unioned rows can carry a practiceName
    // without duplicating demo-mode masking logic inside the redux slice.
    const practiceNames = useMemo(() => {
        if (!selectedPracticeIds) return {};
        const map = {};
        selectedPracticeIds.forEach((id) => {
            const practice = practices?.find((p) => String(p.id) === String(id));
            map[id] = maskName(practice, id);
        });
        return map;
    }, [selectedPracticeIds, practices, maskName]);

    // Fetch when the desired key changes. `fetchKey` (the slot's committed fetch
    // key) is in the deps so a slot reset (Retry, or clearFetchKeys) reliably
    // re-triggers a refetch — resetting fetchKey to null re-runs this effect
    // even when the selection/dateRange are unchanged.
    //
    // CRITICAL — do NOT auto-refetch a key that already FAILED. An error never
    // commits a fetch key (fetchKey stays null), so `fetchKey === desiredKey`
    // alone never becomes true on a persistent error. Because the fetch-start
    // reducer clears errorRequestKey→null and the error sets it back→desiredKey,
    // an error-only guard on errorRequestKey would oscillate and re-dispatch on
    // every cycle — an infinite refetch storm against the API. So once the slot
    // holds an error for the desired key, we STOP and wait for the user's Retry
    // (resetAllPracticesClinicianSlot clears error+errorRequestKey+fetchKey,
    // which re-runs this effect and re-fetches). slotError + slotErrorRequestKey
    // are in the deps so that Retry-driven clear reliably re-triggers.
    const slotError = slotState.error;
    const slotErrorRequestKey = slotState.errorRequestKey;
    useEffect(() => {
        if (!selectedPracticeIds || selectedPracticeIds.length === 0 || !normalizedDateRange) return;
        if (fetchKey === desiredKey) return; // fresh data already committed for this key
        if (slotError && slotErrorRequestKey === desiredKey) return; // already failed this key — await Retry
        reduxDispatch(fetchClinicianConversionData(selectedPracticeIds, normalizedDateRange, {
            slot: 'allPractices',
            practiceNames,
        }));
    }, [practiceIdsKey, normalizedDateRange, fetchKey, desiredKey, slotError, slotErrorRequestKey]);

    // The full page-selected scope, tagged with display names — passed to
    // ClinicianConversionTracker so the Practice column/filter stays
    // authoritative even when 1+ selected practices returned zero rows or
    // failed (Finding 3: the column must never disappear just because a
    // practice didn't respond).
    const practiceScope = useMemo(() => (
        (selectedPracticeIds || []).map((id) => ({ id: String(id), name: practiceNames[id] || String(id) }))
    ), [selectedPracticeIds, practiceNames]);

    const isSinglePractice = selectedPracticeIds?.length === 1;
    const singlePracticeName = isSinglePractice
        ? maskName(practices?.find((p) => String(p.id) === String(selectedPracticeIds[0])), selectedPracticeIds[0])
        : '';
    const titleSuffix = isSinglePractice
        ? singlePracticeName
        : `${selectedPracticeIds?.length || 0} Selected Practices`;
    const exportPracticeName = isSinglePractice ? singlePracticeName : 'Selected Practices';

    const handleRetry = () => {
        reduxDispatch(resetAllPracticesClinicianSlot());
    };

    // Zero selected practices → render nothing at all, no dispatch (guarded by
    // the effect above via the empty-array early return).
    if (!selectedPracticeIds || selectedPracticeIds.length === 0) return null;

    // Render DATA only when the slot's committed fetch key EXACTLY matches the
    // desired key; render the ERROR card only when the slot's error belongs to
    // the desired key; ANY mismatch (including mid-switch, or right after a
    // reselect) renders loading — never a stale selection's data/error under the
    // new selection's heading.
    const isErrorForDesiredKey = Boolean(slotState.error) && slotState.errorRequestKey === desiredKey;
    const isDataForDesiredKey = fetchKey === desiredKey;

    if (isErrorForDesiredKey) {
        return (
            <Box>
                <Box
                    sx={{
                        bgcolor: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        p: 4,
                        mb: 3,
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '16px',
                            bgcolor: '#fee2e2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <Iconify icon="mdi:alert-circle-outline" width={32} sx={{ color: '#ef4444' }} />
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#475569', mb: 1 }}>
                        {`Couldn't load clinician data for ${titleSuffix}`}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#94a3b8', mb: 2 }}>
                        {slotState.error}
                    </Typography>
                    <Button variant="outlined" color="error" onClick={handleRetry}>
                        Retry
                    </Button>
                </Box>
            </Box>
        );
    }

    const isLoading = !isDataForDesiredKey || Boolean(slotState.isLoading);

    return (
        <Box>
            <ClinicianConversionTracker
                key={practiceIdsKey}
                title={`Clinician Conversion Tracker — ${titleSuffix}`}
                data={isDataForDesiredKey ? (slotState.data || []) : []}
                treatments={isDataForDesiredKey ? (slotState.treatments || []) : []}
                teamTotals={isDataForDesiredKey ? (slotState.teamTotals || {}) : {}}
                isLoading={isLoading}
                exportDateRange={dateRange}
                exportPracticeName={exportPracticeName}
                practiceScope={practiceScope}
                failedPractices={isDataForDesiredKey ? (slotState.failedPractices || []) : []}
                selectedCount={isDataForDesiredKey ? (slotState.selectedCount || selectedPracticeIds.length) : selectedPracticeIds.length}
                coveredCount={isDataForDesiredKey ? (slotState.coveredCount || 0) : 0}
                onRetry={handleRetry}
            />
        </Box>
    );
}

AllPracticesClinicianSection.propTypes = {
    practices: PropTypes.arrayOf(PropTypes.object),
    selectedPracticeIds: PropTypes.arrayOf(PropTypes.string),
    dateRange: PropTypes.shape({
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    }),
    maskName: PropTypes.func,
};

AllPracticesClinicianSection.defaultProps = {
    practices: [],
    selectedPracticeIds: [],
    dateRange: null,
    maskName: (practice) => practice?.name || '',
};
